/**
 * Centralized Security Configuration
 * All security-related settings and constants
 */

export const SECURITY_CONFIG = {
  // Rate Limiting
  RATE_LIMIT: {
    CONTACT_FORM: {
      MAX_ATTEMPTS: 3,
      WINDOW_MS: 15 * 60 * 1000, // 15 minutes
      BLOCK_DURATION_MS: 60 * 60 * 1000, // 1 hour
    },
    NEWSLETTER: {
      MAX_ATTEMPTS: 5,
      WINDOW_MS: 60 * 60 * 1000, // 1 hour
      BLOCK_DURATION_MS: 24 * 60 * 60 * 1000, // 24 hours
    }
  },

  // Form Validation
  FORM_LIMITS: {
    FIRST_NAME_MAX: 50,
    LAST_NAME_MAX: 50,
    EMAIL_MAX: 254,
    PHONE_MAX: 20,
    PROJECT_TYPE_MAX: 100,
    LOCATION_MAX: 100,
    DESCRIPTION_MAX: 2000,
    WORK_AMOUNT_MAX: 50
  },

  // Content Security
  CONTENT_SECURITY: {
    MAX_STRING_LENGTH: 1000,
    ALLOWED_HTML_TAGS: [], // No HTML allowed in form submissions
    DANGEROUS_PATTERNS: [
      /<script/i,
      /javascript:/i,
      /on\w+=/i,
      /<iframe/i,
      /<object/i,
      /<embed/i,
      /data:text\/html/i,
      /vbscript:/i
    ]
  },

  // CSRF Protection
  CSRF: {
    TOKEN_LENGTH: 32,
    TOKEN_HEADER: 'X-CSRF-Token',
    STORAGE_KEY: 'csrf_token'
  },

  // Headers and Cookies
  SECURITY_HEADERS: {
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': 'camera=(), microphone=(), geolocation=()'
  },

  // Content Security Policy
  CSP_DIRECTIVES: {
    'default-src': ["'self'"],
    'script-src': ["'self'", "'unsafe-inline'"], // Only for JSON-LD structured data
    'style-src': ["'self'", "'unsafe-inline'"],
    'img-src': ["'self'", "data:", "https:"],
    'font-src': ["'self'"],
    'connect-src': ["'self'", "https://uqjjisbrbasocuhmbviz.supabase.co"],
    'frame-ancestors': ["'none'"],
    'base-uri': ["'self'"],
    'form-action': ["'self'"]
  },

  // Cookie Security
  COOKIES: {
    SECURE: true, // Requires HTTPS in production
    HTTP_ONLY: true,
    SAME_SITE: 'strict' as const,
    MAX_AGE: 7 * 24 * 60 * 60, // 7 days
    PATH: '/'
  },

  // Monitoring and Logging
  MONITORING: {
    LOG_RETENTION_DAYS: 30,
    ALERT_THRESHOLDS: {
      RATE_LIMIT_VIOLATIONS: 5,
      FAILED_SUBMISSIONS: 10,
      SECURITY_EVENTS: 3
    },
    SENSITIVE_FIELDS: [
      'password', 'token', 'secret', 'key', 'auth',
      'email', 'phone', 'address', 'ssn', 'credit',
      'card', 'payment', 'personal', 'private'
    ]
  },

  // Environment-specific settings
  ENVIRONMENT: {
    DEVELOPMENT: {
      ENABLE_CONSOLE_LOGGING: true,
      RELAXED_CSP: false,
      DETAILED_ERRORS: true
    },
    PRODUCTION: {
      ENABLE_CONSOLE_LOGGING: false,
      RELAXED_CSP: false,
      DETAILED_ERRORS: false
    }
  }
} as const;

/**
 * Get environment-specific security configuration
 */
export function getSecurityConfig() {
  const isProduction = process.env.NODE_ENV === 'production';
  const envConfig = isProduction 
    ? SECURITY_CONFIG.ENVIRONMENT.PRODUCTION 
    : SECURITY_CONFIG.ENVIRONMENT.DEVELOPMENT;

  return {
    ...SECURITY_CONFIG,
    CURRENT_ENV: envConfig,
    IS_PRODUCTION: isProduction,
    IS_DEVELOPMENT: !isProduction
  };
}

/**
 * Validate that required security configurations are in place
 */
export function validateSecurityConfig(): boolean {
  const config = getSecurityConfig();
  
  // Check critical security settings
  const criticalChecks = [
    config.CSRF.TOKEN_LENGTH >= 16,
    config.RATE_LIMIT.CONTACT_FORM.MAX_ATTEMPTS > 0,
    config.FORM_LIMITS.EMAIL_MAX > 0,
    config.CONTENT_SECURITY.DANGEROUS_PATTERNS.length > 0
  ];

  return criticalChecks.every(check => check === true);
}

/**
 * Security constants for easy access
 */
export const SECURITY_CONSTANTS = {
  ALLOWED_ORIGINS: [
    'https://uqjjisbrbasocuhmbviz.supabase.co'
  ],
  BLOCKED_USER_AGENTS: [
    /bot/i,
    /crawler/i,
    /spider/i
  ],
  TRUSTED_DOMAINS: [
    'supabase.co',
    'lovable.dev'
  ]
} as const;